/**
 * Client-side alert ring tones.
 * Built-ins use Web Audio with HTML-Audio WAV fallback (autoplay-safe).
 * custom_* keys play uploaded MP3 URLs, then fall back to classic.
 */

export const ALERT_RING_TONES = [
  {
    key: "classic",
    label: "Classic ring",
    description: "Standard dual-tone office ring",
  },
  {
    key: "bright",
    label: "Bright ping",
    description: "Higher, short alert ping",
  },
  {
    key: "soft",
    label: "Soft chime",
    description: "Gentle low chime",
  },
  {
    key: "urgent",
    label: "Urgent beep",
    description: "Fast repeating beeps",
  },
  {
    key: "melody",
    label: "Short melody",
    description: "Three-note ascending melody",
  },
];

export const ALERT_RING_DURATIONS = [2, 3, 5, 8, 10, 15];
export const DEFAULT_DURATION_SEC = 3;

const STORAGE_KEY = "naseebi_agent_alert_sound";
const STORAGE_DURATION_KEY = "naseebi_agent_alert_duration";
const STORAGE_URL_KEY = "naseebi_agent_alert_sound_url";
const CYCLE_MS = 1600;

let audioCtx = null;
let stopTimer = null;
let activeHtmlAudio = null;
let audioUnlocked = false;
let fallbackBeepUrl = null;

function getCtx() {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioCtx || audioCtx.state === "closed") audioCtx = new Ctx();
  return audioCtx;
}

function tone(ctx, freq, start, dur, gainValue = 0.22) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(gainValue, start + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(start);
  osc.stop(start + dur + 0.02);
}

function cyclesForDuration(durationSec) {
  const sec = Number(durationSec) || DEFAULT_DURATION_SEC;
  return Math.max(1, Math.ceil((sec * 1000) / CYCLE_MS));
}

function stopHtmlAudio() {
  if (activeHtmlAudio) {
    try {
      activeHtmlAudio.onended = null;
      activeHtmlAudio.pause();
      activeHtmlAudio.currentTime = 0;
      activeHtmlAudio.removeAttribute("src");
      activeHtmlAudio.load();
    } catch {
      /* ignore */
    }
    activeHtmlAudio = null;
  }
}

function writeString(view, offset, str) {
  for (let i = 0; i < str.length; i += 1) view.setUint8(offset + i, str.charCodeAt(i));
}

/** Build a short dual-tone WAV (classic ring pattern) as a data URL. */
function buildFallbackBeepDataUrl() {
  if (fallbackBeepUrl) return fallbackBeepUrl;
  const sampleRate = 22050;
  const durationSec = 1.2;
  const numSamples = Math.floor(sampleRate * durationSec);
  const dataSize = numSamples * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, "data");
  view.setUint32(40, dataSize, true);

  for (let i = 0; i < numSamples; i += 1) {
    const t = i / sampleRate;
    // Dual-tone ring with envelope so it is clearly audible
    const env = t < 0.05 ? t / 0.05 : t > 0.95 ? (1.2 - t) / 0.25 : 1;
    const sample =
      0.35 * Math.sin(2 * Math.PI * 440 * t) + 0.35 * Math.sin(2 * Math.PI * 480 * t);
    const clamped = Math.max(-1, Math.min(1, sample * env));
    view.setInt16(44 + i * 2, clamped * 0x7fff, true);
  }

  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  fallbackBeepUrl = `data:audio/wav;base64,${btoa(binary)}`;
  return fallbackBeepUrl;
}

async function playHtmlLoop(url, durationSec, { volume = 0.85 } = {}) {
  stopHtmlAudio();
  if (stopTimer) {
    clearTimeout(stopTimer);
    stopTimer = null;
  }

  const audio = new Audio(url);
  audio.loop = true;
  audio.volume = volume;
  activeHtmlAudio = audio;

  const playPromise = audio.play();
  if (playPromise?.then) await playPromise;

  const ms = Math.max(1, Number(durationSec) || DEFAULT_DURATION_SEC) * 1000;
  stopTimer = window.setTimeout(() => {
    stopHtmlAudio();
    stopTimer = null;
  }, ms);
}

async function playMp3Url(url, durationSec) {
  await playHtmlLoop(url, durationSec, { volume: 0.9 });
}

async function playFallbackBeep(durationSec) {
  const url = buildFallbackBeepDataUrl();
  await playHtmlLoop(url, durationSec, { volume: 0.9 });
}

function resolvePlayUrl(soundKey, options = {}) {
  if (options.url) return String(options.url).trim() || null;
  const fromCatalog = options.catalog?.find((t) => t.key === soundKey)?.url;
  if (fromCatalog) return String(fromCatalog).trim() || null;
  if (String(soundKey).startsWith("custom_")) {
    const storedKey = getStoredAlertSoundKey();
    if (storedKey === soundKey) return getStoredAlertSoundUrl();
  }
  return null;
}

async function playWebAudioTone(soundKey, durationSec) {
  const ctx = getCtx();
  if (!ctx) throw new Error("No AudioContext");

  if (ctx.state === "suspended") {
    await ctx.resume();
  }
  if (ctx.state !== "running") {
    throw new Error(`AudioContext is ${ctx.state}`);
  }

  stopHtmlAudio();
  if (stopTimer) {
    clearTimeout(stopTimer);
    stopTimer = null;
  }

  const cycles = cyclesForDuration(durationSec);
  const now = ctx.currentTime;
  const key = String(soundKey);

  for (let c = 0; c < cycles; c += 1) {
    const base = now + c * (CYCLE_MS / 1000);
    if (key === "classic") {
      tone(ctx, 440, base, 0.9, 0.22);
      tone(ctx, 480, base, 0.9, 0.22);
    } else if (key === "bright") {
      tone(ctx, 880, base, 0.18, 0.26);
      tone(ctx, 1175, base + 0.2, 0.18, 0.22);
    } else if (key === "soft") {
      tone(ctx, 523, base, 0.5, 0.16);
      tone(ctx, 659, base + 0.35, 0.55, 0.14);
    } else if (key === "urgent") {
      for (let i = 0; i < 4; i += 1) {
        tone(ctx, 760, base + i * 0.22, 0.12, 0.24);
      }
    } else if (key === "melody") {
      tone(ctx, 523, base, 0.22, 0.2);
      tone(ctx, 659, base + 0.24, 0.22, 0.2);
      tone(ctx, 784, base + 0.48, 0.35, 0.22);
    } else {
      tone(ctx, 440, base, 0.5, 0.22);
      tone(ctx, 480, base, 0.5, 0.22);
    }
  }

  stopTimer = window.setTimeout(() => {
    stopTimer = null;
  }, cycles * CYCLE_MS + 200);
}

/**
 * Preview or play a ring tone by key.
 * @param {string|null} soundKey
 * @param {{ cycles?: number, durationSec?: number, url?: string, catalog?: Array }} [options]
 */
export async function playAlertRingTone(soundKey, options = {}) {
  if (!soundKey || soundKey === "none") return;

  const durationSec = options.durationSec ?? DEFAULT_DURATION_SEC;
  const mp3Url = resolvePlayUrl(soundKey, options);

  // Custom / URL tone first
  if (mp3Url) {
    try {
      await playMp3Url(mp3Url, durationSec);
      return;
    } catch (err) {
      console.warn("[alertRing] MP3 play failed, falling back:", err?.message || err);
    }
  }

  // Built-in Web Audio
  try {
    await playWebAudioTone(soundKey, durationSec);
    return;
  } catch (err) {
    console.warn("[alertRing] WebAudio failed, using WAV fallback:", err?.message || err);
  }

  // HTML Audio WAV — works after any prior unlock / click on the page
  await playFallbackBeep(durationSec);
}

/** Call from a user gesture so later notification rings are allowed to play. */
export async function unlockAlertAudio() {
  if (typeof window === "undefined") return;
  try {
    const ctx = getCtx();
    if (ctx?.state === "suspended") {
      await ctx.resume();
    }
  } catch {
    /* ignore */
  }

  if (audioUnlocked) return;
  try {
    const silent =
      "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAA==";
    const a = new Audio(silent);
    a.volume = 0.01;
    await a.play();
    a.pause();
    audioUnlocked = true;
  } catch {
    // Still mark unlocked attempt — next click will retry via listeners
  }
}

export function stopAlertRingTone() {
  if (stopTimer) {
    clearTimeout(stopTimer);
    stopTimer = null;
  }
  stopHtmlAudio();
  // Do NOT suspend AudioContext — that blocks later notification rings.
}

export function getStoredAlertSoundKey() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && v !== "none" ? v : null;
  } catch {
    return null;
  }
}

export function getStoredAlertSoundUrl() {
  try {
    const v = localStorage.getItem(STORAGE_URL_KEY);
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

export function getStoredAlertDurationSec() {
  try {
    const n = Number(localStorage.getItem(STORAGE_DURATION_KEY));
    if (ALERT_RING_DURATIONS.includes(n)) return n;
    return DEFAULT_DURATION_SEC;
  } catch {
    return DEFAULT_DURATION_SEC;
  }
}

export function setStoredAlertSoundKey(
  soundKey,
  durationSec = DEFAULT_DURATION_SEC,
  soundUrl = null
) {
  try {
    if (!soundKey || soundKey === "none") {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_DURATION_KEY);
      localStorage.removeItem(STORAGE_URL_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, String(soundKey));
      const dur = ALERT_RING_DURATIONS.includes(Number(durationSec))
        ? Number(durationSec)
        : DEFAULT_DURATION_SEC;
      localStorage.setItem(STORAGE_DURATION_KEY, String(dur));
      if (soundUrl) {
        localStorage.setItem(STORAGE_URL_KEY, String(soundUrl));
      } else {
        localStorage.removeItem(STORAGE_URL_KEY);
      }
    }
  } catch {
    /* ignore */
  }
}

export function toneLabel(key, catalog = ALERT_RING_TONES) {
  if (!key) return "None (default)";
  return catalog.find((t) => t.key === key)?.label || key;
}
